// src/lib/gtmSnippet.ts
import analyticsConfig from '@/config/gtmConfig';

// Define more specific dataLayer item type
type DataLayerItem = Record<string, unknown>;

// Declare dataLayer globally for TypeScript
declare global {
  interface Window {
    dataLayer: DataLayerItem[];
  }
}

/**
 * GTM ID for the container from config
 */
export const GTM_ID = analyticsConfig.gtmId;

/**
 * Base URL serving the GTM container (custom domain)
 */
export const GTM_HOST = analyticsConfig.gtmHost;

/**
 * Initializes the data layer array if it doesn't exist
 */
export const initDataLayer = (): void => {
  window.dataLayer = window.dataLayer || [];
};

/**
 * Loads the Google Tag Manager script programmatically from the custom GTM domain
 * @param id GTM container ID
 */
export const loadGTM = (id: string = GTM_ID): void => {
  if (typeof window === 'undefined' || !id) return;

  // Initialize data layer
  initDataLayer();

  // Push initial dataLayer event directly
  window.dataLayer.push({
    'gtm.start': Date.now(),
    event: 'gtm.js',
  });

  // Insert GTM script if it doesn't already exist
  if (!document.getElementById('gtm-script')) {
    const script = document.createElement('script');
    script.id = 'gtm-script';
    script.async = true;

    script.src = `${GTM_HOST}/gtm.js?id=${id}`;

    document.head.insertBefore(script, document.head.firstChild);
  }
};

/**
 * Pushes an event to the dataLayer
 * @param name Event name
 * @param params Additional event parameters
 */
export const pushEvent = (name: string, params: DataLayerItem = {}): void => {
  if (typeof window === 'undefined' || !analyticsConfig.isEnabled) return;

  initDataLayer();

  // Push the event to the dataLayer
  window.dataLayer.push({
    event: name,
    ...params,
  });
};

/**
 * Pushes a page view event to the dataLayer
 * @param path Current path including search parameters
 * @param title Page title
 * @param url Full URL (optional, constructed from path if not provided)
 * @param referrer Referrer URL (optional, uses document.referrer by default)
 */
export const pushPageView = (
  path: string,
  title: string,
  url?: string,
  referrer?: string,
): void => {
  pushEvent('virtual_page_view', {
    page_path: path,
    page_location: url || `${window.location.origin}${path}`,
    page_title: title,
    page_referrer: referrer || document.referrer || '',
  });
};

export default {
  initDataLayer,
  pushEvent,
  pushPageView,
  loadGTM,
};
